import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  PageLayout,
  Typography,
  ButtonsWrapper,
  Button,
  ICONS,
  PLATFORM_THEME_COLORS,
  SearchInput,
  TYPOGRAPHY_SIZE,
  DeleteModal,
  Drawer,
  EmptyContainer,
  EMPTY_ILLUSTRATIONS,
} from '@thinknet-gmbh/platform-common-ui';
import { PLATFORM_PERMISSIONS, PERMISSION_ACTIONS } from '@thinknet-gmbh/platform-common';

import PermissionCheck from 'src/components/permission-check';
import { useGetEmployees } from 'src/hooks/employees/get-employees.hook';
import { useDeleteEmployee } from 'src/hooks/employees/delete-employee.hook';
import { useGetCurrentCompanyId } from 'src/hooks/companies/get-current-company-id.hook';
import { useGetCurrentSubscription } from 'src/hooks/subscriptions/get-current-subscription.hook';

import { HeaderContainer } from './employee.styles';
import { EmployeeListTable } from './components/employee-list-table';
import { EMPLOYEE_MODALS } from './employee.constants';
import { CreateEmployeeModal } from './components/create-employee-modal';

import { EmployeeTableFilter } from './components/employee-filter';

const Employee: React.FC = () => {
  const { t } = useTranslation(['employee', 'common']);
  const [activeModal, setActiveModal] = useState<null | string>(null);
  const [selectedEmployee, setSelectedEmployee] = useState<any>();
  const [searchText, setSearchText] = useState('');
  const [ganttIds, setGanttIds] = useState<string[]>([]);

  const currentCompanyId = useGetCurrentCompanyId();
  const { subscription } = useGetCurrentSubscription();
  const { employees, paginationConfig, onTableChange } = useGetEmployees({
    companyId: currentCompanyId,
    searchParam: searchText,
    ganttIds,
  });

  const deleteEmployeeMutation = useDeleteEmployee({ onSuccess: () => setActiveModal(null) });

  const onSubmitDelete = () => {
    deleteEmployeeMutation({
      variables: {
        payload: {
          _id: selectedEmployee._id,
          companyId: currentCompanyId,
        },
      },
    });

    if (employees.length <= 1 && paginationConfig.current !== 1) {
      paginationConfig.onChange(paginationConfig.current - 1);
    }
  };

  const onSetGanttFilter = (values: any) => {
    setGanttIds(Object.keys(values.gantts).filter((key: string) => !!values.gantts[key]));
    setActiveModal(null);
  };

  const onFilterClose = () => {
    setActiveModal(null);
    setGanttIds([]);
  };

  return (
    <PageLayout pageTitle={t('labels.employee')}>
      {activeModal === EMPLOYEE_MODALS.createEmployee && (
        <CreateEmployeeModal onClose={() => setActiveModal(null)} />
      )}
      {activeModal === EMPLOYEE_MODALS.deleteEmployee && (
        <DeleteModal
          title={t('employee:deleteModal.title')}
          subTitle={t('employee:deleteModal.subtitle', {
            employeeName: `${selectedEmployee?.firstName} ${selectedEmployee?.lastName}`,
          })}
          translate={t}
          onClose={() => setActiveModal(null)}
          onSubmit={onSubmitDelete}
          isOpened
        />
      )}

      <Drawer
        visible={activeModal === EMPLOYEE_MODALS.employeeTableFilter}
        title={t('common:buttons.filterBy')}
        onClose={() => setActiveModal(null)}
      >
        <EmployeeTableFilter
          selected={ganttIds}
          onFinish={onSetGanttFilter}
          onClose={onFilterClose}
        />
      </Drawer>
      <HeaderContainer>
        <Typography weight="bold" size={TYPOGRAPHY_SIZE.medium}>
          {t('employee:labels.employee')}
        </Typography>
        <ButtonsWrapper>
          <SearchInput onChange={e => setSearchText(e.target.value)} />
          <Button
            type="primary"
            ghost
            icon={ICONS.filterAlt}
            iconColor={PLATFORM_THEME_COLORS.primary.blue}
            onClick={() => setActiveModal(EMPLOYEE_MODALS.employeeTableFilter)}
          >
            {t('common:buttons.filterBy')}
          </Button>
          <PermissionCheck
            permission={PLATFORM_PERMISSIONS.employeeManagement}
            action={PERMISSION_ACTIONS.create}
          >
            <Button
              type="primary"
              icon={ICONS.plus}
              disabled={!subscription.allowedEmployeesAddition}
              iconColor={PLATFORM_THEME_COLORS.primary.white}
              onClick={() => setActiveModal(EMPLOYEE_MODALS.createEmployee)}
              {...(!subscription.allowedEmployeesAddition && {
                title: t('common:labels.subscriptionPlacesAlert'),
              })}
            >
              {t('buttons.addNewEmployee')}
            </Button>
          </PermissionCheck>
        </ButtonsWrapper>
      </HeaderContainer>
      {employees.length > 0 ? (
        <EmployeeListTable
          dataSource={employees}
          pagination={paginationConfig}
          onChange={onTableChange}
          deleteEmployee={record => {
            setActiveModal(EMPLOYEE_MODALS.deleteEmployee);
            setSelectedEmployee(record);
          }}
        />
      ) : (
        <EmptyContainer
          illustration={EMPTY_ILLUSTRATIONS.seo}
          emptyTitle={t('common:emptySearch.title')}
          contentText={t('common:emptySearch.subTitle')}
        />
      )}
    </PageLayout>
  );
};

export default Employee;
